"uae client";

import React, { useContext, useState, useMemo } from "react";
import DataContext from "../Context/DAtaContext";
import { motion } from "framer-motion";
import {
  BarChart,
  PieChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Pie,
  Cell,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
// icon imports
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import { FaRegCircleXmark } from "react-icons/fa6";
import {
  FaUsers,
  FaChartBar,
  FaChartLine,
  FaMapMarkerAlt,
  FaUserFriends,
  FaBoxOpen,
  FaClock,
  FaPercentage,
  FaFemale,
  FaMale,
  FaBaby,
  FaHome,
} from "react-icons/fa";
import FarmerOverlay from "../Components/FarmerOverlay";
import { differenceInCalendarDays, parseISO, format } from "date-fns";
import { GrMoney } from "react-icons/gr";

function FarmerFinish({ users }) {
  const { vegetableBundle, Payment, farmerbundle } = useContext(DataContext);
  const { Villages, Zones } = useContext(DataContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [farmerId, setFarmerId] = useState("");
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("overall");

  const handleFarmer = (id) => {
    setFarmerId(id);
    setOpen(true);
  };

  // Calculate all metrics using useMemo for performance
  const dashboardData = useMemo(() => {
    // Base farmer payments calculation
    const farmerPayments = users
      ?.map((fm) => {
        const userPayments = Payment?.filter(
          (py) => py?.farmer_id === fm?.uuid
        );
        const totalAmount = userPayments?.reduce(
          (sum, payment) => sum + payment.amount,
          0
        );

        return {
          farmer_id: fm?.uuid,
          totalAmount,
          name: fm?.name,
          id: fm?.id,
          gender: fm?.gender,
          village_id: fm?.village_id,
          group: fm?.group_membership_status,
          household_size: fm?.number_of_people_in_house_hold,
          children_under_5: fm?.number_of_children_under_5,
          breastfeeding_children: fm?.number_of_breast_feeding_children,
          field_officer: fm?.creator_id,
          payment_date: userPayments?.[0]?.created_at,
          packageName: fm?.prefered_bundle,
          packageType: fm?.prefered_bundle?.toLowerCase()?.includes("poultry")
            ? "poultry"
            : "crop",
        };
      })
      .filter((farmer) => farmer.totalAmount > 0);

    // Finished farmers (those with completed payments)
    const finishedFarmers = farmerPayments
      ?.map((fm) => {
        const Bundle = farmerbundle?.filter(
          (fb) => fb?.farmer_id === fm?.farmer_id
        );
        const VBundle = Bundle?.map((Bd) =>
          vegetableBundle?.filter((vag) => vag?.id === Bd?.bundle_id)
        );
        const FBundle = VBundle.flat();

        const totalProduct = Bundle?.reduce((total, bundle) => {
          const relatedVegBundles = FBundle?.filter(
            (vgb) => vgb?.id === bundle?.bundle_id
          );
          const bundleTotal = relatedVegBundles?.reduce((sum, vegBundle) => {
            return sum + bundle?.amount * vegBundle?.price;
          }, 0);
          return total + bundleTotal;
        }, 0);

        const difference = totalProduct - fm.totalAmount;

        const village = Villages?.find((v) => v?.id === fm?.village_id);
        const FmZone = Zones?.find((z) => z?.id === village?.zone_id);

        if (difference === 0) {
          return {
            ...fm,
            village: village?.name,
            zone_id: FmZone?.id,
            zone: FmZone?.name,
            amountPaid: totalProduct,
            packageItems: Bundle?.length || 0,
            package_created_at: Bundle?.[0]?.created_at,
            package_delivered_at: Bundle?.[0]?.package_delivered_at,
            deliveryStatus:
              Bundle?.[0]?.package_delivered_at !== null
                ? "delivered"
                : "pending",
          };
        }
        return null;
      })
      .filter(Boolean);

    return { farmerPayments, finishedFarmers };
  }, [users, Payment, farmerbundle, vegetableBundle, Zones, Villages]);

  const { finishedFarmers } = dashboardData;

  // OVERALL KPIs
  const overallKPIs = useMemo(() => {
    const totalFinished = finishedFarmers?.length || 0;

    const totalPending =
      finishedFarmers?.filter((f) => f.deliveryStatus === "pending")?.length ||
      0;

    const totalDelivered =
      finishedFarmers?.filter((f) => f.deliveryStatus === "delivered")
        ?.length || 0;

    // Farmers in groups
    const totalInGroups = finishedFarmers?.filter((f) => f.group)?.length || 0;
    const groupPercentage = totalFinished
      ? (totalInGroups / totalFinished) * 100
      : 0;

    // Average package size (based on amount paid)
    const avgPackageSize = totalFinished
      ? finishedFarmers.reduce((sum, f) => sum + f.amountPaid, 0) /
        totalFinished
      : 0;

    // Pending delivery stats
    const pendingDelivery =
      finishedFarmers?.filter((f) => f.deliveryStatus === "pending")?.length ||
      0;
    const pendingPercentage = totalFinished
      ? (pendingDelivery / totalFinished) * 100
      : 0;

    // --- New: Average duration from creation to delivery ---
    const deliveredFarmers = finishedFarmers?.filter(
      (f) =>
        f.deliveryStatus === "delivered" &&
        f.package_created_at &&
        f.package_delivered_at
    );
    const avgDurationDays = deliveredFarmers?.length
      ? deliveredFarmers.reduce((sum, f) => {
          const created = parseISO(f.package_created_at);
          const delivered = parseISO(f.package_delivered_at);
          return sum + differenceInCalendarDays(delivered, created);
        }, 0) / deliveredFarmers.length
      : 0;

    // --- New: Payment completion trend per month ---
    const monthlyTrendMap = {};

    deliveredFarmers?.forEach((f) => {
      const month = format(parseISO(f.package_delivered_at), "MMM yyyy");
      monthlyTrendMap[month] = (monthlyTrendMap[month] || 0) + 1;
    });

    const monthlyTrend = Object.entries(monthlyTrendMap)
      .map(([month, count]) => ({ month, finishers: count }))
      .sort((a, b) => new Date(a.month) - new Date(b.month)); // Optional: sort by month

    return {
      totalFinished,
      totalPending,
      totalDelivered,
      groupPercentage,
      avgPackageSize,
      pendingPercentage,
      avgDurationDays,
      monthlyTrend,
    };
  }, [finishedFarmers]);

  // INVESTMENT KPIs
  const investmentKPIs = useMemo(() => {
    const totalInvestment =
      finishedFarmers?.reduce((sum, f) => sum + f.amountPaid, 0) || 0;

    const avgValuePerPackage = finishedFarmers?.length
      ? totalInvestment / finishedFarmers.length
      : 0;

    // Investment by zone
    const zoneInvestment = finishedFarmers?.reduce((acc, farmer) => {
      const zone = farmer.zone;
      if (!acc[zone]) acc[zone] = { zone, totalInvestment: 0, count: 0 };
      acc[zone].totalInvestment += farmer.amountPaid;
      acc[zone].count += 1;
      return acc;
    }, {});

    const zoneData = Object.values(zoneInvestment || {}).map((z) => ({
      ...z,
      avgInvestment: z.totalInvestment / z.count,
    }));

    const highestInvestor = finishedFarmers?.reduce(
      (max, farmer) =>
        farmer.amountPaid > (max?.amountPaid || 0) ? farmer : max,
      null
    );

    return {
      avgValuePerPackage,
      totalInvestment,
      zoneData,
      highestInvestor,
    };
  }, [finishedFarmers]);

  // DEMOGRAPHIC KPIs
  const demographicKPIs = useMemo(() => {
    const femaleWithFewChildren =
      finishedFarmers?.filter(
        (f) => f.gender === "female" && f.children_under_5 < 5
      )?.length || 0;

    const femalePercentage = finishedFarmers?.length
      ? (femaleWithFewChildren / finishedFarmers.length) * 100
      : 0;

    const avgHouseholdSize = finishedFarmers?.length
      ? finishedFarmers.reduce((sum, f) => sum + f.household_size, 0) /
        finishedFarmers.length
      : 0;

    const withBreastfeedingChildren =
      finishedFarmers?.filter((f) => f.breastfeeding_children > 0)?.length || 0;
    const breastfeedingPercentage = finishedFarmers?.length
      ? (withBreastfeedingChildren / finishedFarmers.length) * 100
      : 0;

    const genderDistribution = [
      {
        gender: "Male",
        count: finishedFarmers?.filter((f) => f.gender === "male")?.length || 0,
      },
      {
        gender: "Female",
        count:
          finishedFarmers?.filter((f) => f.gender === "female")?.length || 0,
      },
    ];

    return {
      femalePercentage,
      avgHouseholdSize,
      breastfeedingPercentage,
      genderDistribution,
    };
  }, [finishedFarmers]);

  // FIELD OFFICER / ZONE KPIs
  const zoneKPIs = useMemo(() => {
    // Group percentage by zone
    const zoneGroupData = finishedFarmers?.reduce((acc, farmer) => {
      const zone = farmer.zone;
      if (!acc[zone]) acc[zone] = { total: 0, inGroups: 0 };
      acc[zone].total += 1;
      if (farmer.group) acc[zone].inGroups += 1;
      return acc;
    }, {});

    const totalFinisherPerZone = Object.entries(
      finishedFarmers?.reduce((acc, farmer) => {
        const zone = farmer.zone;
        if (!acc[zone]) acc[zone] = 0;
        acc[zone] += 1;
        return acc;
      }, {})
    ).map(([zone, total]) => ({
      zone,
      total,
    }));

    const finisherPerVillage = Object.values(
      finishedFarmers?.reduce((acc, farmer) => {
        const { village, gender, zone } = farmer;

        if (!acc[village]) {
          acc[village] = {
            village,
            zone,
            total: 0,
            males: 0,
            females: 0,
          };
        }

        acc[village].total += 1;

        if (gender === "male") acc[village].males += 1;
        else if (gender === "female") acc[village].females += 1;

        return acc;
      }, {})
    );

    const villageStats = finishedFarmers?.reduce(
      (acc, farmer) => {
        acc.total += 1;
        acc.villages.add(farmer.village);
        return acc;
      },
      { total: 0, villages: new Set() }
    );

    const avgFinisherPerVillage = villageStats?.villages.size
      ? villageStats.total / villageStats.villages.size
      : 0;

    const zoneGroupPercentages = Object.entries(zoneGroupData || {}).map(
      ([zone, data]) => ({
        zone,
        groupPercentage: (data.inGroups / data.total) * 100,
        total: data.total,
      })
    );

    // Village performance
    const villageData = finishedFarmers?.reduce((acc, farmer) => {
      const village = farmer.village;
      if (!acc[village]) acc[village] = 0;
      acc[village] += 1;
      return acc;
    }, {});

    const topVillage = Object.entries(villageData || {}).sort(
      ([, a], [, b]) => b - a
    )[0];

    // Field officer performance
    const officerData = finishedFarmers?.reduce((acc, farmer) => {
      const officer = farmer.field_officer;
      if (!acc[officer]) acc[officer] = 0;
      acc[officer] += 1;
      return acc;
    }, {});

    const officerPerformance = Object.entries(officerData || {}).map(
      ([officer, count]) => ({
        officer,
        finishers: count,
        completionRate: Math.random() * 20 + 80, // Simulated completion rate
      })
    );

    return {
      avgFinisherPerVillage,
      finisherPerVillage,
      totalFinisherPerZone,
      zoneGroupPercentages,
      topVillage: topVillage
        ? { village: topVillage[0], count: topVillage[1] }
        : null,
      officerPerformance,
    };
  }, [finishedFarmers]);

  // PACKAGE KPIs
  const packageKPIs = useMemo(() => {
    const packageNames = finishedFarmers?.reduce((acc, farmer) => {
      const type = farmer.packageName;
      if (!acc[type]) acc[type] = 0;
      acc[type] += 1;
      return acc;
    }, {});

    const topPackages = Object.entries(packageNames || {})
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([type, count]) => ({ type, count }));

    const avgItemsPerPackage = finishedFarmers?.length
      ? finishedFarmers.reduce((sum, f) => sum + f.packageItems, 0) /
        finishedFarmers.length
      : 0;

    const poultryCount =
      finishedFarmers?.filter((f) => f.packageType === "poultry")?.length || 0;

    const cropCount =
      finishedFarmers?.filter((f) => f.packageType === "crop")?.length || 0;

    const poultryPercentage = finishedFarmers?.length
      ? (poultryCount / finishedFarmers.length) * 100
      : 0;

    // Package choice by gender
    const genderPackageChoice = finishedFarmers?.reduce((acc, farmer) => {
      if (!acc[farmer.gender]) acc[farmer.gender] = { crop: 0, poultry: 0 };
      acc[farmer.gender][farmer.packageType] += 1;
      return acc;
    }, {});

    return {
      cropCount,
      topPackages,
      avgItemsPerPackage,
      poultryPercentage,
      genderPackageChoice,
    };
  }, [finishedFarmers]);

  // money sorter
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "TZS",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const COLORS = ["#10B981", "#FACC15"]; // Green and Yellow

  const renderSection = () => {
    switch (activeSection) {
      case "overall":
        return (
          <div className="space-y-8">
            {/* Overall KPI Cards */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="p-6 bg-white rounded-lg shadow-md dark:bg-slate-900/50"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      Total Finishers
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-300">
                      {overallKPIs?.totalFinished}
                    </p>
                  </div>
                  <FaUsers className="w-6 h-6 text-green-500" />
                </div>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="p-6 bg-white rounded-lg shadow-md dark:bg-slate-900/50"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      Total Delivered
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-300">
                      {overallKPIs?.totalDelivered}
                    </p>
                  </div>
                  <FaUsers className="w-6 h-6 text-green-500" />
                </div>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="p-6 bg-white rounded-lg shadow-md dark:bg-slate-900/50"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      Total Pending
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-300">
                      {overallKPIs?.totalPending}
                    </p>
                  </div>
                  <FaUsers className="w-6 h-6 text-green-500" />
                </div>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="p-6 bg-white rounded-lg shadow-md dark:bg-slate-900/50 00"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      % in Groups
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-300">
                      {overallKPIs?.groupPercentage.toFixed(1)}%
                    </p>
                  </div>
                  <FaUserFriends className="w-6 h-6 text-green-500" />
                </div>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="p-6 bg-white rounded-lg shadow-md dark:bg-slate-900/50 500"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      Avg Package Size
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-300">
                      {formatCurrency(overallKPIs?.avgPackageSize.toFixed(0))}
                    </p>
                  </div>
                  <FaBoxOpen className="w-6 h-6 text-yellow-500" />
                </div>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="p-6 bg-white rounded-lg shadow-md dark:bg-slate-900/50"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      % Pending Delivery
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-300">
                      {overallKPIs?.pendingPercentage.toFixed(1)}%
                    </p>
                  </div>
                  <FaClock className="w-6 h-6 text-yellow-500" />
                </div>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="p-6 bg-white rounded-lg shadow-md dark:bg-slate-900/50 500"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      Avg Duration
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-300">
                      {overallKPIs?.avgDurationDays?.toFixed(0)} days
                    </p>
                  </div>
                  <FaClock className="w-6 h-6 text-yellow-500" />
                </div>
              </motion.div>
            </div>

            {/* Monthly Trend Chart */}
            <div className="grid grid-cols-1 gap-8 p-4 md:grid-cols-2">
              {/* 📊 Bar Chart: Monthly Completion */}
              <div className="shadow-xs rounded-2xl">
                <h2 className="mb-4 text-lg font-semibold dark:text-gray-300 ">
                  Monthly Payment Completion
                </h2>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={overallKPIs?.monthlyTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Bar
                      dataKey="finishers"
                      fill="#10B981"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* 📈 Area Chart: Average Duration Trend */}
              <div className="rounded-lg shadow-xs">
                <h3 className="invisible mb-4 text-lg font-semibold dark:text-gray-300">
                  Payment Completion Trend
                </h3>
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart data={overallKPIs?.monthlyTrend}>
                    <defs>
                      <linearGradient
                        id="colorFinishers"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#10B981"
                          stopOpacity={0.8}
                        />
                        <stop
                          offset="95%"
                          stopColor="#10B981"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="finishers"
                      stroke="#10B981"
                      strokeWidth={3}
                      fill="url(#colorFinishers)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        );

      case "investment":
        return (
          <div className="space-y-8">
            {/* Investment KPI Cards */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3 xl:grid-cols-4">
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="p-6 bg-white rounded-lg shadow-md dark:bg-slate-900/50 00"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Avg Value/Package
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-300">
                      {formatCurrency(
                        investmentKPIs.avgValuePerPackage.toFixed(0)
                      )}
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="p-6 bg-white rounded-lg shadow-md dark:bg-slate-900/50 0"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Total Investment
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-300">
                      {formatCurrency(investmentKPIs.totalInvestment)}
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="p-6 bg-white rounded-lg shadow-md dark:bg-slate-900/50 500"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Top Zones
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-300">
                      {investmentKPIs.zoneData.length}
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="p-6 bg-white rounded-lg shadow-md dark:bg-slate-900/50 500"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Highest Investor
                    </p>
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-300">
                      {formatCurrency(
                        investmentKPIs.highestInvestor?.amountPaid || 0
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      {investmentKPIs.highestInvestor?.name}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="grid grid-cols-1 gap-8 p-4 md:grid-cols-2">
              {/* Investment by Zone Chart */}
              <div className="rounded-lg shadow-xs ">
                <h3 className="mb-4 text-lg font-semibold dark:text-gray-300">
                  Average Investment per Zone
                </h3>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={investmentKPIs.zoneData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="zone" />
                    <YAxis />
                    <Tooltip
                      formatter={(value) => [
                        `${formatCurrency(value.toFixed(0))}`,
                        "Avg Investment",
                      ]}
                    />
                    <Bar dataKey="avgInvestment" fill="#10B981" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="rounded-lg shadow-xs ">
                <h3 className="invisible mb-4 text-lg font-semibold dark:text-gray-300">
                  Average Investment per Zone
                </h3>
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={investmentKPIs.zoneData}
                      dataKey="avgInvestment"
                      nameKey="zone"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ zone, percent }) =>
                        `${zone} (${(percent * 100).toFixed(0)}%)`
                      }
                    >
                      {investmentKPIs.zoneData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [
                        `${formatCurrency(value.toFixed(0))}`,
                        "Avg Investment",
                      ]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        );

      case "demographic":
        return (
          <div className="space-y-8">
            {/* Demographic KPI Cards */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="p-6 bg-white rounded-lg shadow-md dark:bg-slate-900/50 0"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Female with Under 5 years Children
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-300">
                      {demographicKPIs.femalePercentage.toFixed(1)}%
                    </p>
                  </div>
                  <FaFemale className="w-8 h-8 text-yellow-500" />
                </div>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="p-6 bg-white rounded-lg shadow-md dark:bg-slate-900/50 0"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Avg Household Size
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-300">
                      {demographicKPIs.avgHouseholdSize.toFixed(1)}
                    </p>
                  </div>
                  <FaHome className="w-8 h-8 text-green-500" />
                </div>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="p-6 bg-white rounded-lg shadow-md dark:bg-slate-900/50 00"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      % of Breastfeeding Women
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-300">
                      {demographicKPIs.breastfeedingPercentage.toFixed(1)}%
                    </p>
                  </div>
                  <FaBaby className="w-8 h-8 text-green-500" />
                </div>
              </motion.div>
            </div>

            <div className="grid grid-cols-1 gap-8 p-4 md:grid-cols-2">
              <div className="rounded-lg shadow-xs ">
                <h3 className="mb-4 text-lg font-semibold dark:text-gray-300 ">
                  Gender Distribution
                </h3>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={demographicKPIs.genderDistribution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="gender" />
                    <YAxis />
                    <Tooltip
                      formatter={(value) => [`${value}`, "Count"]}
                      labelFormatter={(label) => `Gender: ${label}`}
                    />
                    <Bar dataKey="count">
                      {demographicKPIs.genderDistribution.map(
                        (entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={index === 0 ? "#10B981" : "#FACC15"}
                          />
                        )
                      )}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Gender Distribution Chart */}
              <div className="rounded-lg shadow-xs ">
                <h3 className="invisible mb-4 text-lg font-semibold dark:text-gray-300">
                  Gender Distribution
                </h3>
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={demographicKPIs.genderDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ gender, count, percent }) =>
                        `${gender}: ${count} (${(percent * 100).toFixed(0)}%)`
                      }
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {demographicKPIs.genderDistribution.map(
                        (entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={index === 0 ? "#10B981" : "#FACC15"}
                          />
                        )
                      )}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        );

      case "zones":
        return (
          <div className="space-y-8">
            {/* Top Village Card */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {zoneKPIs?.topVillage && (
                <div className="p-6 bg-white rounded-lg shadow-md dark:bg-slate-900/50">
                  <h3 className="mb-2 text-lg font-semibold dark:text-gray-300">
                    Top Performing Village
                  </h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-300">
                        {zoneKPIs.topVillage.village}
                      </p>
                      <p className="text-xl text-gray-600 dark:text-gray-400">
                        {zoneKPIs.topVillage.count} finishers
                      </p>
                    </div>
                    <FaMapMarkerAlt className="text-green-500 w-9 h-9" />
                  </div>
                </div>
              )}

              <div className="p-6 bg-white rounded-lg shadow-md dark:bg-slate-900/50">
                <h3 className="mb-2 text-lg font-semibold dark:text-gray-300">
                  Finished Farmers per Zone
                </h3>
                <div className="flex flex-col items-center justify-between md:flex-row">
                  {zoneKPIs?.totalFinisherPerZone?.map((zone) => (
                    <div key={zone.zone} className="flex flex-col items-center">
                      <div>
                        <p className="text-gray-600 dark:text-gray-400">
                          {zone.zone}
                        </p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-300">
                          {zone.total}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-100 dark:bg-slate-900/50">
                  <tr>
                    <th className="px-6 py-4 text-xs font-semibold tracking-wider text-left text-gray-900 uppercase dark:text-white">
                      #
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold tracking-wider text-left text-gray-900 uppercase dark:text-white">
                      Zone
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold tracking-wider text-left text-gray-900 uppercase dark:text-white">
                      Village
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold tracking-wider text-center text-gray-900 uppercase dark:text-white">
                      Total Farmers
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold tracking-wider text-center text-gray-900 uppercase dark:text-white">
                      Males
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold tracking-wider text-center text-gray-900 uppercase dark:text-white">
                      Females
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-900/20 dark:divide-gray-700">
                  {zoneKPIs?.finisherPerVillage?.map((village, index) => (
                    <tr
                      key={village.village}
                      className="transition-colors duration-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <td className="px-6 py-4 text-sm font-medium text-gray-500 whitespace-nowrap dark:text-gray-400">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap dark:text-white">
                        {village.zone}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap dark:text-white">
                        {village.village}
                      </td>
                      <td className="px-6 py-4 text-center whitespace-nowrap">
                        <span className="px-3 py-1 text-sm font-semibold text-gray-900 bg-gray-100 rounded-full dark:text-white dark:bg-gray-700">
                          {village.total}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center whitespace-nowrap">
                        <span className="px-3 py-1 text-sm font-semibold text-blue-700 rounded-full dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30">
                          {village.males}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center whitespace-nowrap">
                        <span className="px-3 py-1 text-sm font-semibold text-pink-700 rounded-full dark:text-pink-400 bg-pink-50 dark:bg-pink-900/30">
                          {village.females}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="p-6 bg-white rounded-lg shadow-xs dark:bg-slate-900/50">
              <h3 className="mb-2 text-lg font-semibold dark:text-gray-300">
                Avgerage finishers per village
              </h3>
              <div className="flex flex-col items-center justify-between md:flex-row">
                <div className="flex flex-col items-center">
                  <div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-300">
                      {zoneKPIs?.avgFinisherPerVillage.toFixed(1)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-8 p-4 md:grid-cols-2">
              {/* Zone Performance */}
              <div className="rounded-lg shadow-xs ">
                <h3 className="mb-4 text-lg font-semibold dark:text-gray-300">
                  % finished who are in groups per zone
                </h3>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={zoneKPIs.zoneGroupPercentages}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="zone" />
                    <YAxis />
                    <Tooltip
                      formatter={(value) => [
                        `${value.toFixed(1)}%`,
                        "Group Participation",
                      ]}
                    />
                    <Bar dataKey="groupPercentage" fill="#10B981" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="w-full shadow-xs h-80 dark:bg-gray-800 rounded-xl">
                <h3 className="mb-4 text-lg font-semibold dark:text-gray-300">
                  total finished farmers per zone
                </h3>

                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={zoneKPIs?.totalFinisherPerZone}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
                    <XAxis dataKey="zone" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="total" fill="#10B981" />{" "}
                    {/* Tailwind green-500 */}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        );

      case "packages":
        return (
          <div className="space-y-8">
            {/* Package KPI Cards */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="p-6 bg-white rounded-lg shadow-md dark:bg-slate-900/50 00"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Avg Items/Package
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-300">
                      {packageKPIs.avgItemsPerPackage.toFixed(1)}
                    </p>
                  </div>
                  <FaBoxOpen className="w-8 h-8 text-green-500" />
                </div>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="p-6 bg-white rounded-lg shadow-md dark:bg-slate-900/50 0"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      % Poultry Packages
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-300">
                      {packageKPIs.poultryPercentage.toFixed(1)}%
                    </p>
                  </div>
                  <FaPercentage className="w-8 h-8 text-green-500" />
                </div>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="p-6 bg-white rounded-lg shadow-md dark:bg-slate-900/50 500"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Package Types
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-300">
                      {packageKPIs.topPackages.length}
                    </p>
                  </div>
                  <FaChartBar className="w-8 h-8 text-green-500" />
                </div>
              </motion.div>
            </div>

            <div className="grid grid-cols-1 gap-5 p-4 md:grid-cols-2">
              {/* Top Packages Chart */}
              <div className="rounded-lg shadow-xs ">
                <h3 className="mb-4 text-lg font-semibold dark:text-gray-300">
                  Most Selected Packages
                </h3>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={packageKPIs.topPackages}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="type" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#10B981" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* Package Choice by Gender */}
              <div className="rounded-lg shadow-xs ">
                <h3 className="mb-4 text-lg font-semibold dark:text-gray-300">
                  Package Choice by Gender
                </h3>
                <ResponsiveContainer width="100%" height={370}>
                  <BarChart
                    data={Object.entries(
                      packageKPIs.genderPackageChoice || {}
                    ).map(([gender, choices]) => ({
                      gender,
                      crop: choices.crop,
                      poultry: choices.poultry,
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="gender" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="crop"
                      stackId="a"
                      fill="#10B981"
                      name="Crop Packages"
                    />
                    <Bar
                      dataKey="poultry"
                      stackId="a"
                      fill="#F59E0B"
                      name="Poultry Packages"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Search functionality
  const filteredFarmers = finishedFarmers?.filter(
    (farmer) =>
      farmer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      farmer?.zone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      farmer?.village?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen p-6">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-gray-900 dark:text-gray-300">
            Finished Farmers Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Comprehensive analytics for farmers who have completed their payment
            cycles
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="p-1 mb-8 dark:bg-gray-700">
          <nav className="flex space-x-8">
            {[
              { id: "overall", label: "Overall", icon: FaChartLine },
              { id: "investment", label: "Investment", icon: GrMoney },
              { id: "demographic", label: "Demographics", icon: FaUsers },
              { id: "zones", label: "Zones", icon: FaMapMarkerAlt },
              { id: "packages", label: "Packages", icon: FaBoxOpen },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveSection(id)}
                className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeSection === id
                    ? "bg-green-600 text-white dark:bg-gray-900/50  "
                    : "text-gray-500 dark:text-gray-300 hover:text-gray-200 hover:bg-gray-800"
                }`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {label}
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        {renderSection()}

        {/* Farmers List Section */}
        <div className="mt-12 bg-white rounded-lg shadow-md dark:bg-slate-900/60">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-gray-300 sm:mb-0">
                Finished Farmers List ({finishedFarmers?.length || 0})
              </h2>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search farmers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-2 pl-10 pr-4 rounded-lg outline-none sm:w-64 dark:bg-gray-800 dark:text-gray-200 "
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto h-[400px]">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 ">
              <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-900 ">
                <tr>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-900 uppercase dark:text-gray-200">
                    Farmer
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-900 uppercase dark:text-gray-200">
                    Amount Paid
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-900 uppercase dark:text-gray-200">
                    Zone/Village
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-900 uppercase dark:text-gray-200">
                    Package
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-900 uppercase dark:text-gray-200">
                    Status
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-900 uppercase dark:text-gray-200">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 dark:divide-gray-700 dark:bg-slate-900/20">
                {filteredFarmers?.map((farmer, index) => (
                  <motion.tr
                    key={farmer.farmer_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="transition-colors duration-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 w-10 h-10">
                          <div className="flex items-center justify-center w-10 h-10 bg-gray-300 rounded-full">
                            {farmer.gender === "female" ? (
                              <FaFemale className="w-5 h-5 text-yellow-500" />
                            ) : (
                              <FaMale className="w-5 h-5 text-green-500" />
                            )}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-300">
                            {farmer.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            ID: {farmer.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-200">
                        TZS {farmer?.amountPaid?.toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {farmer.packageItems} items
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-200">
                        {farmer.zone}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {farmer.village}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          farmer.packageType === "poultry"
                            ? " text-yellow-800"
                            : " text-green-800"
                        }`}
                      >
                        {farmer.packageName}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {farmer.deliveryStatus === "delivered" ? (
                          <IoMdCheckmarkCircleOutline className="w-5 h-5 mr-2 text-green-500" />
                        ) : (
                          <FaRegCircleXmark className="w-5 h-5 mr-2 text-red-500" />
                        )}
                        <span
                          className={`text-sm ${
                            farmer.deliveryStatus === "delivered"
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          {farmer.deliveryStatus === "delivered"
                            ? "Delivered"
                            : "Pending"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium whitespace-nowrap">
                      <button
                        onClick={() => handleFarmer(farmer?.id)}
                        className="text-blue-600 transition-colors hover:text-blue-900"
                      >
                        View Details
                      </button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredFarmers?.length === 0 && (
            <div className="py-12 text-center">
              <div className="mb-2 text-lg text-gray-400">No farmers found</div>
              <p className="text-gray-500">
                {searchTerm
                  ? "Try adjusting your search criteria"
                  : "No finished farmers available"}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* farmer overlay */}
      <FarmerOverlay FarmerId={farmerId} open={open} setOpen={setOpen} />
    </div>
  );
}

export default FarmerFinish;
